import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowDown, Send, LockKeyhole, BriefcaseBusiness } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar } from './Avatar';
import { UserName, useMessaging } from './MessagingContext';
import { OnlineDot } from './Presence';
import { useAuth } from '../auth/AuthContext';
import { useI18n } from '../i18n';
import { chatApi, messageDate } from '../lib/chat';

const MessageBubble = ({ message, mine }) => {
  const { t, locale } = useI18n();
  if (message.kind === 'job_accepted') return <div data-testid={`chat-message-${message.id}`} className="mx-auto max-w-lg w-full rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 my-3">
    <div className="flex gap-2 text-amber-500 text-[10px] font-mono uppercase tracking-widest mb-2"><BriefcaseBusiness size={14} className="shrink-0" />{t('chat.auto')}</div>
    <p className="text-sm text-slate-200 leading-relaxed [overflow-wrap:anywhere]">{message.text}</p>
    <div className="flex flex-wrap gap-2 justify-between items-center mt-3"><Link data-testid={`chat-job-${message.id}`} to={`/ofertas/${message.job_id}`} className="text-xs text-amber-500 hover:underline">{t('chat.job')} →</Link><time className="text-[10px] text-slate-500">{messageDate(message.created_at, locale)}</time></div>
  </div>;
  return <div data-testid={`chat-message-${message.id}`} data-mine={mine} className={`max-w-[88%] sm:max-w-[75%] w-fit rounded-2xl px-4 py-3 ${mine ? 'ml-auto bg-[#302619] border border-amber-500/20 rounded-br-sm' : 'bg-[#1A1D24] border border-[#2A2F3D] rounded-bl-sm'}`}>
    <p className="text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere] text-slate-100">{message.text}</p>
    <time className="block mt-1.5 text-right text-[10px] text-slate-400 font-mono">{messageDate(message.created_at, locale)}</time>
  </div>;
};

const MessageComposer = ({ id, disabled, onSent }) => {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(null);
  const sending = useRef(false);
  const submit = async (e) => {
    e.preventDefault();
    if (sending.current || disabled || !text.trim()) return;
    sending.current = true; setBusy(true); setError('');
    if (!pending.current || pending.current.text !== text.trim()) pending.current = { text: text.trim(), client_id: crypto.randomUUID() };
    try {
      const message = await chatApi(`/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify(pending.current) });
      pending.current = null; setText(''); onSent(message);
    } catch (err) { setError(err.message); } finally { sending.current = false; setBusy(false); }
  };
  return <form onSubmit={submit} className="border-t border-[#2A2F3D] p-3 sm:p-4 bg-[#121418] shrink-0" data-testid="chat-composer">
    {error && <p role="alert" data-testid="chat-send-error" className="text-xs text-red-300 mb-2">{error}</p>}
    <div className="flex items-end gap-2"><textarea data-testid="chat-message-input" aria-label={t('chat.placeholder')} placeholder={t('chat.placeholder')} value={text} maxLength={4000} rows={2} disabled={disabled || busy} onChange={(e) => setText(Array.from(e.target.value).slice(0, 4000).join(''))} className="min-w-0 flex-1 resize-none bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg p-3 text-sm disabled:opacity-50" />
      <Button type="submit" data-testid="chat-send-button" disabled={disabled || busy || !text.trim()} className="h-11 shrink-0 px-3" aria-label={t(busy ? 'chat.sending' : 'chat.submit')}><Send size={16} /><span className="hidden sm:inline">{t(busy ? 'chat.sending' : 'chat.submit')}</span></Button>
    </div><div className="flex justify-between gap-3 mt-2 text-[10px] text-slate-500 font-mono"><span data-testid="chat-message-limit">{t('chat.limit')}</span><span data-testid="chat-message-length" aria-live="polite" className={Array.from(text).length >= 4000 ? 'text-amber-400' : ''}>{Array.from(text).length}/4000</span></div>
  </form>;
};

export const ChatThread = ({ id, onActivity }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { refreshUnread } = useMessaging();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [olderBusy, setOlderBusy] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [newBelow, setNewBelow] = useState(false);
  const feed = useRef(null);
  const latest = useRef('');
  const readId = useRef('');
  const atBottom = useRef(true);
  const oldHeight = useRef(null);
  const forceScroll = useRef(false);
  const markRead = useCallback(async () => {
    if (document.hidden || !atBottom.current || !latest.current || latest.current === readId.current) return;
    const last = latest.current;
    try { await chatApi(`/conversations/${id}/read`, { method: 'PUT', body: JSON.stringify({ last_read_id: last }) }); readId.current = last; refreshUnread(); onActivity(); } catch { /* Retry receipt on the next polling cycle. */ }
  }, [id, refreshUnread, onActivity]);
  const merge = useCallback((items) => {
    if (!items.length) return;
    latest.current = items.reduce((last, m) => m.id > last ? m.id : last, latest.current);
    setMessages((prev) => [...new Map([...prev, ...items].map((m) => [m.id, m])).values()].sort((a, b) => a.id.localeCompare(b.id)));
    if (!atBottom.current) setNewBelow(true);
  }, []);
  useEffect(() => {
    let active = true, inFlight = false;
    const controller = new AbortController();
    const tick = async () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      try {
        if (!latest.current) {
          const [details, data] = await Promise.all([chatApi(`/conversations/${id}`, { signal: controller.signal }), chatApi(`/conversations/${id}/messages`, { signal: controller.signal })]);
          if (!active) return;
          setConversation(details); setHasOlder(data.has_more); merge(data.items); setLoading(false);
        } else {
          let more = true;
          while (more && active) {
            const data = await chatApi(`/conversations/${id}/messages?after=${latest.current}`, { signal: controller.signal });
            if (!active) return;
            merge(data.items); more = data.has_more;
          }
        }
        if (active) { setError(''); await markRead(); }
      } catch (err) { if (active) { setError(err.message); setLoading(false); } } finally { inFlight = false; }
    };
    tick(); const timer = setInterval(tick, 5000);
    document.addEventListener('visibilitychange', tick);
    return () => { active = false; controller.abort(); clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, [id, attempt, merge, markRead]);
  useLayoutEffect(() => {
    const el = feed.current;
    if (!el) return;
    if (oldHeight.current !== null) { el.scrollTop += el.scrollHeight - oldHeight.current; oldHeight.current = null; }
    else if (atBottom.current || forceScroll.current) { el.scrollTop = el.scrollHeight; atBottom.current = true; forceScroll.current = false; setNewBelow(false); }
  }, [messages]);
  const loadOlder = async () => {
    setOlderBusy(true);
    try {
      const data = await chatApi(`/conversations/${id}/messages?before=${messages[0].id}`);
      oldHeight.current = feed.current.scrollHeight;
      setMessages((prev) => [...new Map([...data.items, ...prev].map((m) => [m.id, m])).values()]); setHasOlder(data.has_more);
    } catch (err) { setError(err.message); } finally { setOlderBusy(false); }
  };
  const peer = conversation?.contact;
  return <section data-testid="chat-thread" className="flex flex-col min-h-0 min-w-0 h-full relative">
    <header className="flex items-center gap-3 px-3 sm:px-5 py-4 border-b border-[#2A2F3D] shrink-0 bg-[#121418] min-w-0">
      <Link to="/mensajes" data-testid="chat-back" aria-label={t('chat.back')} className="md:hidden text-slate-400 p-1"><ArrowLeft size={20} /></Link>
      {peer && <><Avatar src={peer.avatar_url} name={peer.name} size={40} /><div className="min-w-0 flex-1"><UserName userId={peer.available ? peer.user_id : null} testId="chat-contact-name" className="font-semibold text-white block max-w-full truncate">{peer.name}</UserName><span className="text-xs text-slate-500 truncate block">{peer.is_admin ? t('chat.admin') : peer.role || t(peer.account_type === 'company' ? 'chat.company' : 'chat.member')}</span></div><OnlineDot userId={peer.user_id} /></>}
    </header>
    {error && <div role="alert" data-testid="chat-load-error" className="px-4 py-2 text-xs text-red-300 border-b border-red-500/20">{error}<Button variant="link" data-testid="chat-retry" onClick={() => setAttempt((v) => v + 1)}>{t('chat.retry')}</Button></div>}
    <div ref={feed} data-testid="chat-message-feed" role="log" aria-label={t('chat.title')} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-3" onScroll={() => { const el = feed.current; atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 70; if (atBottom.current) { setNewBelow(false); markRead(); } }}>
      {loading && <p data-testid="chat-thread-loading" className="text-sm text-slate-500">{t('chat.loading')}</p>}
      {hasOlder && <div className="text-center"><Button data-testid="chat-load-older" variant="ghost" size="sm" disabled={olderBusy} onClick={loadOlder}>{t(olderBusy ? 'chat.loading' : 'chat.older')}</Button></div>}
      {!loading && !messages.length && !error && <div data-testid="chat-no-messages" className="text-center text-slate-500 text-sm pt-12"><LockKeyhole size={24} className="mx-auto mb-3 text-amber-500/50" />{t('chat.noMessages')}</div>}
      {messages.map((message) => <MessageBubble key={message.id} message={message} mine={message.sender_id === user.user_id} />)}
    </div>
    {newBelow && <Button data-testid="chat-new-below" variant="secondary" className="absolute bottom-36 self-center shadow-xl" onClick={() => { feed.current.scrollTop = feed.current.scrollHeight; atBottom.current = true; setNewBelow(false); markRead(); }}><ArrowDown size={14} />{t('chat.newMessages')}</Button>}
    {peer && !peer.available && <p data-testid="chat-unavailable" className="p-3 text-xs text-slate-400">{t('chat.unavailable')}</p>}
    <MessageComposer id={id} disabled={!peer?.available} onSent={(message) => { forceScroll.current = true; merge([message]); onActivity(); }} />
  </section>;
};
